import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// Cache configuration
const ADMIN_CACHE_TTL = 30 * 1000; // 30 seconds - much shorter for immediate updates
const ADMIN_CACHE_KEY = 'adminStatusCache';

interface AdminCacheEntry {
  isAdmin: boolean;
  timestamp: number;
  email: string;
}

// In-memory cache
let adminCache: AdminCacheEntry | null = null;

/**
 * Custom hook to check if the current user is an admin
 * @returns Object containing isAdmin flag and loading state
 */
export const useIsAdmin = () => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async (forceRefresh = false) => {
      if (forceRefresh) {
        // Clear cache immediately
        adminCache = null;
        localStorage.removeItem(ADMIN_CACHE_KEY);
      }
      if (!user || !user.email) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const userEmail = user.email.toLowerCase();
      const now = Date.now();

      // Check memory cache first (skip if force refresh)
      if (!forceRefresh && adminCache &&
          adminCache.email === userEmail &&
          now - adminCache.timestamp < ADMIN_CACHE_TTL) {
        setIsAdmin(adminCache.isAdmin);
        setLoading(false);
        return;
      }

      // Check localStorage cache (skip if force refresh)
      if (!forceRefresh) {
        try {
          const cachedData = localStorage.getItem(ADMIN_CACHE_KEY);
          if (cachedData) {
            const parsed: AdminCacheEntry = JSON.parse(cachedData);
            if (parsed.email === userEmail && now - parsed.timestamp < ADMIN_CACHE_TTL) {
              adminCache = parsed;
              setIsAdmin(parsed.isAdmin);
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      try {
        // Use direct document lookup with email as document ID
        const { doc, getDoc } = await import('firebase/firestore');
        const emailDocRef = doc(db, 'allowedEmails', userEmail);
        const docSnapshot = await getDoc(emailDocRef);
        
        let hasAdminAccess = false;
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          hasAdminAccess = data.isAdmin === true;
        }

        // Cache the result
        const cacheEntry: AdminCacheEntry = {
          isAdmin: hasAdminAccess,
          timestamp: now,
          email: userEmail
        };
        adminCache = cacheEntry;
        localStorage.setItem(ADMIN_CACHE_KEY, JSON.stringify(cacheEntry));

        setIsAdmin(hasAdminAccess);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
    
    // Listen for force refresh events
    const handleForceRefresh = () => {
      checkAdminStatus(true);
    };
    
    window.addEventListener('forceAuthRefresh', handleForceRefresh);
    
    return () => {
      window.removeEventListener('forceAuthRefresh', handleForceRefresh);
    };
  }, [user]);

  return { isAdmin, loading };
};

// Export function to clear admin cache (useful when admin status changes)
export const clearAdminCache = () => {
  adminCache = null;
  localStorage.removeItem(ADMIN_CACHE_KEY);
};
